import React, { useEffect, useState } from 'react';
import { View, Text, Pressable, StyleSheet, ActivityIndicator, useWindowDimensions } from 'react-native';
import theme from '@/config/theme';
import { databaseService } from '@/services/databaseService';
import { APP_CONSTANTS } from '@/config/constants';
import ChecklistMediaObservation from './ChecklistMediaObservation';

interface ChecklistDropdownFieldProps {
  checklistId: string;
  fieldId: string;
  tipo: string;
  label: string;
  value?: string;
  onChange: (val: string) => void;
  observation?: string;
  onObservationChange?: (val: string) => void;
  editable?: boolean;
  observationEditable?: boolean;
}

/** As opções continuam vindo do catálogo; tocar na escolha marcada limpa somente a escolha. */
const ChecklistDropdownField = ({ fieldId, tipo, label, value, onChange, observation = '', onObservationChange, editable = true, observationEditable = false }: ChecklistDropdownFieldProps) => {
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [choicesWidth, setChoicesWidth] = useState(0);
  const { width } = useWindowDimensions();
  const narrow = width < 390;
  useEffect(() => {
    let mounted = true;
    setLoading(true); setFailed(false); setOptions([]);
    databaseService.listItems<{ valor: string }>(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_DROPDOWN.NAME, { tipo })
      .then(rows => { if (mounted) setOptions(rows.map(row => row.valor)); })
      .catch(() => { if (mounted) setFailed(true); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [tipo]);
  const longestOption = Math.max(0, ...options.map(option => option.length));
  const minimumWidth = Math.min(160, Math.max(64, longestOption * 6 + 16));
  const columns = Math.min(options.length === 4 ? 2 : 3, Math.max(1, Math.floor((choicesWidth + 4) / (minimumWidth + 4))));
  const rows = Array.from({ length: Math.ceil(options.length / columns) }, (_, index) => options.slice(index * columns, (index + 1) * columns));
  return <View style={styles.container}>
    <View style={[styles.line, narrow && styles.stacked]}>
      <Text style={[styles.label, narrow && styles.wideLabel]}>{label}</Text>
      <View style={styles.choices} onLayout={event => setChoicesWidth(event.nativeEvent.layout.width)}>
        {loading ? <ActivityIndicator accessibilityLabel="Carregando opções" /> : failed ? <Text>Não foi possível carregar as opções.</Text> : rows.map((row, index) => <View key={index} style={styles.optionRow}>
          {row.map(option => {
            const selected = value === option;
            const normalized = option.toLocaleLowerCase('pt-BR');
            const color = /danificado|não ok|ruim|ausente/.test(normalized) ? '#a53636' : /médio|regular|atenção/.test(normalized) ? '#a45d00' : '#267448';
            return <Pressable key={option} accessibilityRole="radio" accessibilityLabel={`${label}: ${option}`} accessibilityState={{ checked: selected, disabled: !editable }}
              testID={`choice-${fieldId}-${option}`} disabled={!editable} onPress={() => onChange(selected ? '' : option)}
              style={[styles.option, selected && { backgroundColor: color, borderColor: color }]}>
              <Text style={[styles.optionText, selected && styles.selectedText]}>{option}</Text>
            </Pressable>;
          })}
          {Array.from({ length: columns - row.length }, (_, i) => <View key={`empty-${i}`} style={styles.emptyOption} />)}
        </View>)}
        {value && !options.includes(value) && !loading ? <Text style={styles.legacyValue}>Escolha salva: {value}</Text> : null}
      </View>
    </View>
    <ChecklistMediaObservation fieldId={fieldId} label={label} value={observation}
      editable={editable && observationEditable && Boolean(onObservationChange)}
      onChange={note => onObservationChange?.(note)} />
  </View>;
};

const styles = StyleSheet.create({
  container: { marginBottom: 12, paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: theme.colors.border.light },
  line: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  stacked: { flexDirection: 'column', gap: 0 },
  label: { width: '29%', minWidth: 0, color: theme.colors.text.secondary, fontSize: 13, paddingTop: 10 },
  wideLabel: { width: '100%', paddingTop: 0, marginBottom: theme.components.checklistEditor.spacing.labelGap },
  choices: { flex: 1, minWidth: 0, alignSelf: 'stretch' },
  optionRow: { flexDirection: 'row', gap: 4, marginBottom: 4 },
  option: { flex: 1, minWidth: 0, minHeight: 44, paddingHorizontal: 3, paddingVertical: 8, borderRadius: 6, borderWidth: 1, borderColor: '#e2e7ec', backgroundColor: '#edf0f4', alignItems: 'center', justifyContent: 'center' },
  emptyOption: { flex: 1 },
  optionText: { fontSize: 12, color: '#465268', textAlign: 'center' },
  selectedText: { color: '#fff', fontWeight: '600' },
  legacyValue: { fontSize: 12, color: theme.colors.text.secondary },

});
export default ChecklistDropdownField;
